// Lazy load pages
import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/Layout';
import { useAuthStore } from './stores/auth';

const Home = lazy(() => import('./pages/Home'));
const Events = lazy(() => import('./pages/Events'));
const Event = lazy(() => import('./pages/Event'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Help = lazy(() => import('./pages/Help'));
const Shop = lazy(() => import('./pages/Shop'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Auth = lazy(() => import('./pages/Auth'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminEventEdit = lazy(() => import('./pages/AdminEventEdit'));
const Referrals = lazy(() => import('./pages/Referrals'));

// Loading fallback
function PageLoader() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--color-bg-primary)',
      }}
    >
      <div style={{ fontSize: '48px', animation: 'pulse 1.5s infinite' }}>🎯</div>
    </div>
  );
}

// Optional auth check - only redirects for pages that require auth
function OptionalAuthRoute({
  children,
  requireAuth = false,
}: {
  children: React.ReactNode;
  requireAuth?: boolean;
}) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return <PageLoader />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const { checkAuth } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes without layout */}
        <Route path="/auth" element={<Auth />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/events" element={<AdminEvents />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/events/:id" element={<AdminEventEdit />} />

        {/* All routes with layout */}
        <Route path="/" element={<Layout />}>
          {/* Public routes - no auth required */}
          <Route path="events" element={<Events />} />
          <Route path="events/:id" element={<Event />} />
          <Route path="leaderboard" element={<Leaderboard />} />

          {/* Protected routes - auth required */}
          <Route
            index
            element={
              <OptionalAuthRoute requireAuth>
                <Home />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="profile"
            element={
              <OptionalAuthRoute requireAuth>
                <Profile />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="settings"
            element={
              <OptionalAuthRoute requireAuth>
                <Settings />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <OptionalAuthRoute requireAuth>
                <Notifications />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="help"
            element={
              <OptionalAuthRoute requireAuth>
                <Help />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="shop"
            element={
              <OptionalAuthRoute requireAuth>
                <Shop />
              </OptionalAuthRoute>
            }
          />
          <Route
            path="referrals"
            element={
              <OptionalAuthRoute requireAuth>
                <Referrals />
              </OptionalAuthRoute>
            }
          />
        </Route>

        {/* Catch all - redirect to events */}
        <Route path="*" element={<Navigate to="/events" replace />} />
      </Routes>
    </Suspense>
  );
}
